// FILE: src/layouts/MainLayout.jsx
// The signed-in app shell (plan WP-8 task 4, matrix 8.1 / 10.2):
// - skip link → <main id="main-content">; landmarks: header, nav, main, footer;
// - side navigation: permanent drawer ≥ md, temporary drawer below md;
//   the current page is marked with aria-current, bold text, the primary
//   tint and an edge bar (never colour alone);
// - top bar: light/dark switch, account menu (profile, display settings,
//   sign out); above it, the "new version available" notice;
// - students: profile/onboarding gate and the enrolment-ended page (MTO 6.3,
//   support stays reachable after expiry).
import React, { useCallback, useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TuneIcon from "@mui/icons-material/Tune";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import SessionGuard from "../components/SessionGuard";
import { checkSession, logoutUser } from "../features/authSlice";
import { fetchProfile } from "../features/profileSlice";
import StudentCompleteInfo from "../pages/StudentCompleteInfoPage";
import EnrolmentEndedPage from "../pages/EnrolmentEndedPage";
import Wordmark from "../components/brand/Wordmark";
import ThemeToggle from "../components/shell/ThemeToggle";
import DisplaySettingsDialog from "../components/shell/DisplaySettings";
import { ADMIN_NAV, STUDENT_NAV, isActive, staffHome, visibleSections } from "../components/shell/navigation";
import usePermissions from "../hooks/usePermissions";
import { ErrorState, LoadingState } from "../components/ui";
import GlossaryDrawer from "../components/help/GlossaryDrawer";
import NewVersionBanner from "../components/shell/NewVersionBanner";
import OnboardingPage from "../pages/OnboardingPage";
import { fetchOnboarding } from "../features/onboardingSlice";

export const DRAWER_WIDTH = 272;
// Pages an expired student can still use (MTO 6.3: support stays reachable).
const OPEN_AFTER_EXPIRY = ["/student/support", "/student/profile", "/student/activity", "/student/help", "/student/glossary", "/student/course-info", "/student/system-requirements", "/student/accessibility"];
// Pages that need the getting-started steps done first (MTO 12.4–12.6): the
// course homepage and the lessons. Help, support and profile stay open.
const needsOnboarding = (path) => path === "/student" || path === "/student/" || path.startsWith("/student/resources");

function initials(user) {
  const a = (user?.firstName || "").trim()[0] || "";
  const b = (user?.lastName || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

export function SkipLink() {
  return (
    <Box
      component="a"
      href="#main-content"
      sx={{
        position: "absolute",
        left: 16,
        top: -64,
        zIndex: (t) => t.zIndex.tooltip + 1,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        px: 2,
        py: 1.25,
        borderRadius: 2,
        fontWeight: 650,
        textDecoration: "underline",
        "&:focus": { top: 12 }
      }}
    >
      Skip to main content
    </Box>
  );
}

function NavList({ sections, pathname, onNavigate }) {
  return (
    <Box component="nav" aria-label="Main" sx={{ py: 1 }}>
      {sections.map((section) => {
        const headingId = `nav-${section.heading.replace(/\W+/g, "-").toLowerCase()}`;
        return (
          <Box key={section.heading} sx={{ py: 0.5 }}>
            <Typography
              id={headingId}
              component="h2"
              sx={{ color: "text.secondary", fontSize: "1rem", fontWeight: 600, lineHeight: 2.5, px: 3 }}
            >
              {section.heading}
            </Typography>
            <List aria-labelledby={headingId} sx={{ py: 0 }}>
              {section.items.map((item) => {
                const active = isActive(item, pathname);
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <ListItemButton
                      component={RouterLink}
                      to={item.path}
                      selected={active}
                      aria-current={active ? "page" : undefined}
                      onClick={onNavigate}
                    >
                      <ListItemIcon aria-hidden>
                        <Icon />
                      </ListItemIcon>
                      <ListItemText slotProps={{ primary: { fontWeight: active ? 650 : 500, fontSize: "1rem" } }} primary={item.label} />
                    </ListItemButton>
                  </li>
                );
              })}
            </List>
          </Box>
        );
      })}
    </Box>
  );
}

function SidebarContent({ sections, pathname, onNavigate, isStudent, home }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar sx={{ px: 2.5, minHeight: { xs: 64, md: 72 } }}>
        <Link
          component={RouterLink}
          to={home}
          underline="none"
          aria-label="Zun Drivers, go to dashboard"
          onClick={onNavigate}
          sx={{ display: "block", borderRadius: 2 }}
        >
          <Wordmark />
        </Link>
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <NavList sections={sections} pathname={pathname} onNavigate={onNavigate} />
      </Box>
      {isStudent && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ p: 2, borderRadius: 3, bgcolor: "zun.primaryTint" }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
              <SupportAgentOutlinedIcon aria-hidden sx={{ color: "primary.main" }} />
              <Typography sx={{ fontWeight: 650 }}>Need help?</Typography>
            </Stack>
            <Typography sx={{ color: "text.secondary", mb: 1 }}>
              Technical, service and course-content support, with phone, email and hours.
            </Typography>
            <Link component={RouterLink} to="/student/support" onClick={onNavigate}>
              Contact support
            </Link>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function AccountMenu({ user, role, onSignOut }) {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [displayOpen, setDisplayOpen] = useState(false);
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Your account";
  const close = () => setAnchor(null);

  return (
    <>
      <Tooltip title="Account">
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={`Account menu for ${name}`}
          aria-haspopup="menu"
          aria-expanded={anchor ? "true" : undefined}
          aria-controls={anchor ? "account-menu" : undefined}
          sx={{ ml: 0.5 }}
        >
          <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main", color: "primary.contrastText", fontSize: "1rem", fontWeight: 700 }}>
            {initials(user)}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        id="account-menu"
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 1, border: "1px solid", borderColor: "divider" } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 650 }}>{name}</Typography>
          <Typography sx={{ color: "text.secondary" }}>{role === "admin" ? user?.staffRole?.name || "Staff" : "Student"}</Typography>
        </Box>
        <Divider />
        {role !== "admin" && (
          <MenuItem
            onClick={() => {
              close();
              navigate("/student/profile");
            }}
          >
            <ListItemIcon>
              <PersonOutlineIcon />
            </ListItemIcon>
            My profile
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            close();
            setDisplayOpen(true);
          }}
        >
          <ListItemIcon>
            <TuneIcon />
          </ListItemIcon>
          Display &amp; accessibility
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            close();
            onSignOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
      <DisplaySettingsDialog open={displayOpen} onClose={() => setDisplayOpen(false)} />
    </>
  );
}

export function SiteFooter() {
  const role = useSelector((s) => s.auth.role);
  return (
    <Box
      component="footer"
      sx={{ mt: 6, pt: 3, borderTop: "1px solid", borderColor: "divider", color: "text.secondary" }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 3 }} sx={{ flexWrap: "wrap" }}>
        <Typography>© {new Date().getFullYear()} Zun Drivers</Typography>
        <Link
          component={RouterLink}
          to={role === "student" ? "/student/system-requirements" : "/system-requirements"}
          color="text.secondary"
        >
          System requirements
        </Link>
        <Link component={RouterLink} to={role === "student" ? "/student/help" : "/help"} color="text.secondary">
          Help centre
        </Link>
        <Link component={RouterLink} to={role === "student" ? "/student/accessibility" : "/accessibility"} color="text.secondary">
          Accessibility
        </Link>
        <Link component={RouterLink} to="/privacy" color="text.secondary">
          Privacy
        </Link>
      </Stack>
    </Box>
  );
}

export default function Layout({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const { role, user } = useSelector((state) => state.auth);
  const { profile, loading, error } = useSelector((state) => state.profile);
  const enrolmentExpired = useSelector((state) => state.time.enrolmentExpired);
  const onboarding = useSelector((state) => state.onboarding);
  const { pathname } = useLocation();
  const isStudent = role === "student";
  const { can } = usePermissions();
  const home = isStudent ? "/student" : staffHome(can) || "/admin";

  const handleSignOut = useCallback(() => {
    dispatch(logoutUser());
    navigate("/login");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (isStudent && user && !profile && !loading && !error) {
      dispatch(fetchProfile());
    }
  }, [isStudent, profile, loading, error, user, dispatch]);

  useEffect(() => {
    dispatch(checkSession());
  }, [dispatch]);

  const profileReady = !!(profile?.address && profile?.hasConsent);
  useEffect(() => {
    if (isStudent && profileReady && !onboarding.data && !onboarding.loading && !onboarding.error) {
      dispatch(fetchOnboarding());
    }
  }, [isStudent, profileReady, onboarding.data, onboarding.loading, onboarding.error, dispatch]);

  // Close the phone drawer after navigating.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  let mainContent = children;
  if (isStudent) {
    if (error && !profile) {
      mainContent = <ErrorState error={error} title="We couldn't load your profile" onRetry={() => dispatch(fetchProfile())} />;
    } else if (!profile) {
      mainContent = <LoadingState variant="page" label="Loading your profile…" />;
    } else if (enrolmentExpired && !OPEN_AFTER_EXPIRY.some((p) => pathname.startsWith(p))) {
      mainContent = <EnrolmentEndedPage />;
    } else if (!profile.address || !profile.hasConsent) {
      mainContent = <StudentCompleteInfo />;
    } else if (needsOnboarding(pathname) && onboarding.data && !onboarding.data.complete) {
      mainContent = <OnboardingPage />;
    }
  }

  // Staff see only the pages their role allows (RBAC).
  const sections = isStudent ? STUDENT_NAV : visibleSections(ADMIN_NAV, can);
  const sidebar = (
    <SidebarContent
      sections={sections}
      pathname={pathname}
      isStudent={isStudent}
      home={home}
      onNavigate={desktop ? undefined : () => setMobileOpen(false)}
    />
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <SkipLink />

      {/* Side navigation */}
      <Box component="div" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {desktop ? (
          <Drawer
            variant="permanent"
            open
            sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" } }}
          >
            {sidebar}
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" } }}
          >
            {sidebar}
          </Drawer>
        )}
      </Box>

      {/* Content column */}
      <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <NewVersionBanner />
        <AppBar position="sticky" component="header">
          <Toolbar sx={{ gap: 1, minHeight: { xs: 64, md: 72 }, px: { xs: 1.5, sm: 3 } }}>
            {!desktop && (
              <>
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open navigation menu"
                  aria-expanded={mobileOpen ? "true" : "false"}
                  edge="start"
                >
                  <MenuIcon />
                </IconButton>
                <Link
                  component={RouterLink}
                  to={home}
                  underline="none"
                  aria-label="Zun Drivers, go to dashboard"
                  sx={{ display: "block", minWidth: 0 }}
                >
                  <Wordmark size={28} compact />
                </Link>
              </>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {isStudent && <GlossaryDrawer compact={!desktop} />}
            <ThemeToggle />
            <AccountMenu user={user} role={role} onSignOut={handleSignOut} />
          </Toolbar>
        </AppBar>

        {/* minWidth 0: wide tables scroll inside main instead of widening the page on phones */}
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            width: "100%",
            maxWidth: 1240,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 3, md: 4 },
            "&:focus": { outline: "none" }
          }}
        >
          {/* Idle timeout, identity challenge, break/cap notices, expiry banner */}
          {user && <SessionGuard />}
          {mainContent}
          <SiteFooter />
        </Box>
      </Box>
    </Box>
  );
}
